<template>
    <div class="spotlight-result-item" :class="{ 'spotlight-result-item--focused': focused }" @click="$emit('select', item)">
        <div v-if="thumbUrl" class="spotlight-result-icon spotlight-result-icon--file spotlight-result-icon--file-preview">
            <img :src="thumbUrl" alt="" />
        </div>
        <div v-else class="spotlight-result-icon spotlight-result-icon--file">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
        </div>
        <div class="spotlight-result-text">
            <div class="spotlight-result-title">{{ item.title }}</div>
            <div class="spotlight-result-subtitle spotlight-file-subtitle">
                <span class="spotlight-file-ext">{{ fileTypeLabel }}</span>
                <template v-if="durationText">
                    <span class="spotlight-inline-sep">&middot;</span>
                    <span>{{ durationText }}</span>
                </template>
                <template v-if="item.meta?.size_text">
                    <span class="spotlight-inline-sep">&middot;</span>
                    <span>{{ item.meta.size_text }}</span>
                </template>
            </div>
        </div>
        <div v-if="relations.length" class="spotlight-result-meta spotlight-file-meta-cycle">
            <transition name="spotlight-rel-fade" mode="out-in">
                <span :key="activeIndex" class="spotlight-file-relation" :class="'spotlight-file-relation--' + relationType(activeRelation)">
                    <svg v-if="relationType(activeRelation) === 'note'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <svg v-else-if="relationType(activeRelation) === 'workorder'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                    <svg v-else-if="relationType(activeRelation) === 'folder'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                    <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    <span class="spotlight-file-relation-label">{{ activeRelation.label }}</span>
                </span>
            </transition>
        </div>
        <div v-else class="spotlight-result-meta">
            <span class="spotlight-result-meta-muted">{{ $t('common.spotlight.fileOrphan') }}</span>
        </div>
    </div>
</template>

<script>
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif', 'tiff', 'tif']
const VIDEO_EXT = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v']
const AUDIO_EXT = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'wma', 'm4a']
const DOC_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'txt', 'rtf', 'csv']
const ARCHIVE_EXT = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2']

function resolveFileType(ext) {
    const e = (ext || '').toLowerCase()
    if (IMAGE_EXT.includes(e)) return 'Resim'
    if (VIDEO_EXT.includes(e)) return 'Video'
    if (AUDIO_EXT.includes(e)) return 'Ses'
    if (e === 'pdf') return 'PDF'
    if (DOC_EXT.includes(e)) return 'Belge'
    if (ARCHIVE_EXT.includes(e)) return 'Arşiv'
    return e.toUpperCase()
}

const TYPE_MAP = {
    MODULE_NOTE: 'note',
    MODULE_VEHICLE_WORK_ORDER: 'workorder',
    FILE_MANAGER_FOLDER: 'folder',
}

export default {
    name: 'FileResult',
    props: { item: Object, focused: Boolean },
    emits: ['select'],
    data() {
        return { activeIndex: 0 }
    },
    computed: {
        isImage() {
            return IMAGE_EXT.includes((this.item.subtitle || '').toLowerCase())
        },
        thumbUrl() {
            const group = this.item.meta?.extension_detail?.group
            if (group === 'image' || group === 'sound') {
                const t = this.item.meta?.thumb
                const url = t && (t.small || t.xsmall || t.medium || t.large)
                if (url && !url.includes('/system/icon/')) return url
            }
            if (this.isImage && this.item.meta?.public_url) return this.item.meta.public_url
            return null
        },
        durationText() {
            const d = this.item.meta?.duration
            if (d == null || d <= 0) return ''
            const total = Math.floor(d)
            const h = Math.floor(total / 3600)
            const m = Math.floor((total % 3600) / 60)
            const s = total % 60
            const pad = n => String(n).padStart(2, '0')
            return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
        },
        fileTypeLabel() {
            return resolveFileType(this.item.subtitle)
        },
        relations() {
            return this.item.meta?.relations || []
        },
        activeRelation() {
            return this.relations[this.activeIndex] || this.relations[0]
        },
        shouldCycle() {
            return this.relations.length > 1
        }
    },
    watch: {
        shouldCycle: {
            immediate: true,
            handler(val) {
                this._clearCycle()
                if (val) {
                    this._cycleTimer = setInterval(() => {
                        this.activeIndex = (this.activeIndex + 1) % this.relations.length
                    }, 2500)
                }
            }
        }
    },
    beforeUnmount() {
        this._clearCycle()
    },
    methods: {
        relationType(rel) {
            return TYPE_MAP[rel.type] || 'link'
        },
        _clearCycle() {
            if (this._cycleTimer) {
                clearInterval(this._cycleTimer)
                this._cycleTimer = null
            }
        }
    }
}
</script>
